import React, { useState, useEffect, useRef } from 'react';
import { Carriage } from 'types';
import { Checkbox, Row } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { Constants } from 'core';

import { getCarriageBackgroundImage } from 'common/utils/carriage-image';
import { useIntl } from 'common/config/i18n';
import Card from 'components/molecules/card';
import FormError from 'components/molecules/form-error';

import CarriageSelection from '../carriage-selection';
import { Container, Content, Props, StyledTooltip, Wrapper, getNumberCarriages } from './utils';

const TargetCarriages = ({
  formik,
  onTouch,
  selectedServiceType,
  equipementCarriages,
  isVisible,
}: Props): React.ReactElement => {
  const { t } = useIntl();

  const [selectedFirstTrainCarriages, setSelectedFirstTrainCarriages] = useState<Carriage[]>([]);
  const [selectedSecondTrainCarriages, setSelectedSecondTrainCarriages] = useState<Carriage[]>([]);
  const [defaultFirstTrainCarriages, setDefaultFirstTrainCarriages] = useState<Carriage[]>([]);
  const [defaultSecondTrainCarriages, setDefaultSecondTrainCarriages] = useState<Carriage[]>([]);
  const [trainWidth, setTrainWidth] = useState<number>(0);

  const carriageRef = useRef<HTMLDivElement>(null);

  const isOuigoSlowOrMargrite = ['OUIGO_LC', 'OUIGO_LB'].includes(selectedServiceType || '');

  // this useEffect reinitialise the values if goBack from 2nd step to 1st step
  useEffect(() => {
    if (
      !!formik.values?.selectedCarriages?.length &&
      !selectedFirstTrainCarriages.length &&
      !selectedSecondTrainCarriages.length
    ) {
      const selectedFirstTrainCarriagesDefaultValue = formik.values?.selectedCarriages
        .filter((item) => Number(item.carriageNumber) <= getNumberCarriages(selectedServiceType || 'OUIGO'))
        .map((item) => ({
          ...item,
          wholeCarriageSelected: ['OUIGO', 'OUIGO_ES'].includes(selectedServiceType || '')
            ? !!item.downStage && !!item.upStage
            : true,
        }));
      const selectedSecondTrainCarriagesDefaultValue = formik.values?.selectedCarriages
        .filter((item) => Number(item.carriageNumber) > getNumberCarriages(selectedServiceType || 'OUIGO'))
        .map((item) => ({
          ...item,
          wholeCarriageSelected: ['OUIGO', 'OUIGO_ES'].includes(selectedServiceType || '')
            ? !!item.downStage && !!item.upStage
            : true,
        }));

      setDefaultFirstTrainCarriages(selectedFirstTrainCarriagesDefaultValue);
      setDefaultSecondTrainCarriages(selectedSecondTrainCarriagesDefaultValue);

      setFirstTrain(selectedFirstTrainCarriagesDefaultValue);
      setSecondTrain(selectedSecondTrainCarriagesDefaultValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values?.selectedCarriages]);

  useEffect(() => {
    const selectedCarriages = [
      ...selectedFirstTrainCarriages.map((item) => ({
        carriageNumber: item.carriageNumber,
        downStage: item.downStage,
        upStage: item.upStage,
      })),
      ...selectedSecondTrainCarriages.map((item) => ({
        carriageNumber: item.carriageNumber,
        downStage: item.downStage,
        upStage: item.upStage,
      })),
    ];
    formik.setFieldValue('selectedCarriages', selectedCarriages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFirstTrainCarriages, selectedSecondTrainCarriages]);

  useEffect(() => {
    if (carriageRef?.current?.clientWidth) {
      isOuigoSlowOrMargrite
        ? setTrainWidth(carriageRef?.current?.clientWidth * 2)
        : setTrainWidth(carriageRef?.current?.clientWidth * 1.55);
    }
  }, [setTrainWidth, carriageRef, isOuigoSlowOrMargrite]);

  const setFirstTrain = (train: Carriage[]): void => {
    setSelectedFirstTrainCarriages(train);
  };
  const setSecondTrain = (train: Carriage[]): void => {
    setSelectedSecondTrainCarriages(train);
  };

  const onChangeCheckIncludePassengers = (e: CheckboxChangeEvent) => {
    formik.setFieldValue('includePassengersWithoutSeats', e.target.checked);
  };

  return (
    <Container isVisible={isVisible}>
      <Card full title={t('form', 'CUSTOMER_SELECTION_IN_COACHES_H3')}>
        <FormError formik={formik} fieldName="selectedCarriages" />
        <Row>
          <Content
            style={{
              backgroundImage: `url(${getCarriageBackgroundImage(selectedServiceType || 'OUIGO')})`,
              backgroundPosition: isOuigoSlowOrMargrite ? '50% 52%' : '0 100%',
              backgroundRepeat: 'no-repeat',
              paddingLeft: isOuigoSlowOrMargrite ? '15rem' : '9.375rem',
              width: trainWidth,
            }}
          >
            {selectedServiceType === 'OUIGO' && (
              <Wrapper ref={carriageRef}>
                <CarriageSelection
                  values={Constants.TRAIN_CONFIGURATION.OUIGO.FIRST_TRAIN}
                  trainName={t('list_circulation', 'CHART_WAGON_1')}
                  arrowLeft
                  withMultipleFloors
                  setSelectedCarriages={setFirstTrain}
                  onTouch={onTouch}
                  defaultCarriages={defaultFirstTrainCarriages}
                  equipementCarriages={equipementCarriages}
                  selectedServiceType={selectedServiceType}
                />
                <CarriageSelection
                  values={Constants.TRAIN_CONFIGURATION.OUIGO.SECOND_TRAIN}
                  trainName={t('list_circulation', 'CHART_WAGON_2')}
                  withMultipleFloors
                  setSelectedCarriages={setSecondTrain}
                  onTouch={onTouch}
                  defaultCarriages={defaultSecondTrainCarriages}
                  equipementCarriages={equipementCarriages}
                  selectedServiceType={selectedServiceType}
                />
              </Wrapper>
            )}
            {selectedServiceType === 'OUIGO_ESP' && (
              <Wrapper ref={carriageRef}>
                <CarriageSelection
                  values={Constants.TRAIN_CONFIGURATION.OUIGO_ESP.FIRST_TRAIN}
                  trainName={t('list_circulation', 'CHART_WAGON_1')}
                  arrowLeft
                  withMultipleFloors
                  setSelectedCarriages={setFirstTrain}
                  onTouch={onTouch}
                  defaultCarriages={defaultFirstTrainCarriages}
                  equipementCarriages={equipementCarriages}
                  selectedServiceType={selectedServiceType}
                />
                <CarriageSelection
                  values={Constants.TRAIN_CONFIGURATION.OUIGO_ESP.SECOND_TRAIN}
                  trainName={t('list_circulation', 'CHART_WAGON_2')}
                  withMultipleFloors
                  setSelectedCarriages={setSecondTrain}
                  onTouch={onTouch}
                  defaultCarriages={defaultSecondTrainCarriages}
                  equipementCarriages={equipementCarriages}
                  selectedServiceType={selectedServiceType}
                />
              </Wrapper>
            )}
            {selectedServiceType === 'OUIGO_LC' && (
              <Wrapper ref={carriageRef}>
                <CarriageSelection
                  values={Constants.TRAIN_CONFIGURATION.OUIGO_LC}
                  trainName={t('common', 'all')}
                  arrowLeft
                  setSelectedCarriages={setFirstTrain}
                  onTouch={onTouch}
                  defaultCarriages={defaultFirstTrainCarriages}
                  equipementCarriages={equipementCarriages}
                  selectedServiceType={selectedServiceType}
                />
              </Wrapper>
            )}
            {selectedServiceType === 'OUIGO_LB' && (
              <Wrapper ref={carriageRef}>
                <CarriageSelection
                  values={Constants.TRAIN_CONFIGURATION.OUIGO_LB}
                  trainName={t('common', 'all')}
                  arrowLeft
                  setSelectedCarriages={setFirstTrain}
                  onTouch={onTouch}
                  defaultCarriages={defaultFirstTrainCarriages}
                  equipementCarriages={equipementCarriages}
                  selectedServiceType={selectedServiceType}
                />
              </Wrapper>
            )}
          </Content>
          <StyledTooltip
            title={t('sendCommunication', 'carriagesSelection', 'includePassengersTooltip')}
            placement="top"
          >
            <Checkbox
              checked={formik.values.includePassengersWithoutSeats}
              className="includePassengersCheckBox"
              onChange={onChangeCheckIncludePassengers}
            >
              {t('sendCommunication', 'carriagesSelection', 'includePassengers')}
            </Checkbox>
          </StyledTooltip>
        </Row>
      </Card>
    </Container>
  );
};

export default TargetCarriages;
